import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Camera, X, FlashlightOff, Flashlight, RotateCcw, Loader2 } from 'lucide-react';

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.ITF,
];

const BarcodeScanner = ({ onScan, onClose, theme }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [currentCameraIdx, setCurrentCameraIdx] = useState(0);
  const scannerRef = useRef(null);
  const scannerContainerId = 'barcode-scanner-container';

  useEffect(() => {
    // Get available cameras
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length > 0) {
          // Prefer back camera
          const backCameraIdx = devices.findIndex(
            (d) => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear')
          );
          setCameras(devices);
          setCurrentCameraIdx(backCameraIdx >= 0 ? backCameraIdx : 0);
        } else {
          setError('No cameras found on this device');
        }
      })
      .catch((err) => {
        console.error('Error getting cameras:', err);
        setError('Unable to access camera. Please check permissions.');
      });

    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    if (!cameras.length) return;

    setError(null);
    setIsScanning(true);

    try {
      const scanner = new Html5Qrcode(scannerContainerId, {
        formatsToSupport: SUPPORTED_FORMATS,
        verbose: false,
      });
      scannerRef.current = scanner;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 150 },
        aspectRatio: 1.5,
        disableFlip: false,
      };

      await scanner.start(
        cameras[currentCameraIdx].id,
        config,
        (decodedText, decodedResult) => {
          // Successful scan
          console.log('Barcode scanned:', decodedText, decodedResult);
          
          // Vibrate if supported
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          
          // Stop scanning and return result
          stopScanner();
          onScan(decodedText, decodedResult.result.format?.formatName || 'unknown');
        },
        (errorMessage) => {
          // Scan error (this fires constantly while scanning, ignore)
        }
      );

      // Check if flash is available
      const track = scanner.getRunningTrackSettings?.();
      if (track && 'torch' in track) {
        setHasFlash(true);
      }
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setIsScanning(false);
      setError(err.message || 'Failed to start camera');
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
    setFlashOn(false);
  };

  const toggleFlash = async () => {
    if (scannerRef.current && hasFlash) {
      try {
        if (flashOn) {
          await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: false }] });
        } else {
          await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: true }] });
        }
        setFlashOn(!flashOn);
      } catch (err) {
        console.error('Failed to toggle flash:', err);
      }
    }
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    
    await stopScanner();
    const nextIdx = (currentCameraIdx + 1) % cameras.length;
    setCurrentCameraIdx(nextIdx);
    
    // Restart with new camera after a brief delay
    setTimeout(() => {
      startScanner();
    }, 100);
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      onClick={(e) => {
        // Click-outside-to-close (only on backdrop)
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h2 className="text-white font-semibold">Scan Barcode</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="text-white hover:bg-white/20"
          data-testid="close-scanner-btn"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {error ? (
          <Card className="w-full max-w-sm">
            <CardContent className="p-6 text-center">
              <div className="text-red-500 mb-4">{error}</div>
              <Button onClick={startScanner} style={{ backgroundColor: theme?.primary }}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : !isScanning ? (
          <Card className="w-full max-w-sm">
            <CardContent className="p-6 text-center">
              <Camera className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 mb-4">
                Point your camera at a barcode to scan it
              </p>
              <Button 
                onClick={startScanner} 
                className="w-full"
                style={{ backgroundColor: theme?.primary, color: theme?.dark }}
                data-testid="start-scanner-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                Start Scanning
              </Button>
              <p className="text-xs text-gray-500 mt-4">
                Supports UPC, EAN, QR codes, Code128 and more
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="w-full max-w-md">
            {/* Scanner viewport */}
            <div 
              id={scannerContainerId} 
              className="w-full rounded-lg overflow-hidden"
              style={{ minHeight: '300px' }}
            />
            
            {/* Scanning indicator */}
            <div className="flex items-center justify-center mt-4 text-white">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span>Scanning...</span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      {isScanning && (
        <div className="flex items-center justify-center gap-4 p-4 bg-black/50">
          {hasFlash && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFlash}
              className="text-white hover:bg-white/20"
              data-testid="toggle-flash-btn"
            >
              {flashOn ? (
                <Flashlight className="h-6 w-6" />
              ) : (
                <FlashlightOff className="h-6 w-6" />
              )}
            </Button>
          )}
          
          {cameras.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={switchCamera}
              className="text-white hover:bg-white/20"
              data-testid="switch-camera-btn"
            >
              <RotateCcw className="h-6 w-6" />
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={handleClose}
            className="bg-white/10 text-white border-white/30"
            data-testid="cancel-scan-btn"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Help text */}
      <div className="p-4 text-center text-white/70 text-sm bg-black/50">
        Hold the barcode steady within the scanning area
      </div>
    </div>
  );
};

export default BarcodeScanner;
